import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function LobbyNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteUserId = searchParams.get("invite");
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [difficulty, setDifficulty] = useState("normal");
  const [rounds, setRounds] = useState(10);
  const [seconds, setSeconds] = useState(30);
  const [gameMode, setGameMode] = useState("classic");
  const [includeClubs, setIncludeClubs] = useState(true);
  const [includeNT, setIncludeNT] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.post("/lobbies", {
        max_players: maxPlayers,
        settings: {
          game_mode: gameMode,
          // Phase-9.3: Quiz draws from a unified pool across all difficulties.
          difficulty: gameMode === "quiz" ? null : difficulty,
          rounds_count: rounds,
          time_per_round_sec: seconds,
          include_clubs: includeClubs,
          include_national_teams: includeNT,
        },
      });
      toast.success(`Lobby ${data.lobby_code} created`);
      // Phase 5.4: if the create flow was triggered via Dashboard "Friends online"
      // "Create & invite" CTA, fire the friend invite before navigating.
      if (inviteUserId) {
        try {
          await api.post(`/lobbies/${data.lobby_code}/invite-friend`, { to_user_id: inviteUserId });
        } catch (e3) {
          toast.error(e3?.response?.data?.detail || "Auto-invite failed");
        }
      }
      navigate(`/lobby/${data.lobby_code}`, { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not create lobby");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="lobby-new-page" className="min-h-screen bg-[var(--bg)] text-zinc-100">
      <header className="px-6 py-5 max-w-2xl mx-auto">
        <Link to="/dashboard" className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">
          ← dashboard
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 pb-16">
        <h1 className="text-4xl font-bold">Create a lobby</h1>
        <p className="text-sm text-zinc-400 mt-2">
          You&apos;ll be the host. Invite friends with the 6-character code or a shareable link.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-widest text-zinc-500">Max players</label>
              <span className="text-sm font-mono" data-testid="new-max-players-value">{maxPlayers}</span>
            </div>
            <input
              data-testid="new-max-players"
              type="range"
              min={2}
              max={8}
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(Number(e.target.value))}
              className="mt-2 w-full accent-[var(--accent)]"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Mode</label>
            <div className="mt-2 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Game mode">
              {[
                { value: "classic", label: "Classic", sub: "3 teammate cards" },
                { value: "career_path", label: "Career Path", sub: "trace the timeline" },
                { value: "quiz", label: "Quiz", sub: "trivia, no cards" },
              ].map((m) => (
                <button
                  key={m.value}
                  type="button"
                  role="radio"
                  aria-checked={gameMode === m.value}
                  onClick={() => setGameMode(m.value)}
                  data-testid={`new-mode-${m.value}`}
                  className={`px-4 py-3 rounded-xl border text-left transition ${
                    gameMode === m.value
                      ? "bg-[var(--accent)] text-black border-[var(--accent)] font-bold shadow-[0_0_24px_-6px_var(--accent)]"
                      : "border-white/10 hover:bg-white/5"
                  }`}
                >
                  <div className="text-sm font-bold">{m.label}</div>
                  <div className={`text-[10px] uppercase tracking-widest mt-0.5 ${gameMode === m.value ? "text-black/60" : "text-zinc-500"}`}>
                    {m.sub}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {gameMode !== "quiz" ? (
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Difficulty</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {["normal", "hard", "insane"].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDifficulty(d)}
                    data-testid={`new-difficulty-${d}`}
                    className={`px-3 py-2.5 rounded-xl border text-sm capitalize transition ${
                      difficulty === d
                        ? "bg-[var(--accent)] text-black border-[var(--accent)] font-bold"
                        : "border-white/10 hover:bg-white/5"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="new-quiz-mixed-pool-note"
                 className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-[11px] text-zinc-400">
              Quiz mode draws random questions across all difficulties — no setting needed.
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Rounds</label>
              <select
                data-testid="new-rounds"
                value={rounds}
                onChange={(e) => setRounds(Number(e.target.value))}
                className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition appearance-none cursor-pointer"
              >
                {[3, 5, 10, 15, 20].map((n) => (
                  <option key={n} value={n}>{n} rounds</option>
                ))}
              </select>
              <div className="text-[11px] text-zinc-500 mt-1">pick a length</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Seconds / round</label>
              <div className="mt-1 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Seconds per round">
                {[30, 45, 60].map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={seconds === v}
                    onClick={() => setSeconds(v)}
                    data-testid={`new-seconds-${v}`}
                    className={`px-3 py-2.5 rounded-xl border text-sm transition font-mono ${
                      seconds === v
                        ? "bg-[var(--accent)] text-black border-[var(--accent)] font-bold shadow-[0_0_24px_-6px_var(--accent)]"
                        : "border-white/10 text-zinc-300 hover:bg-white/5"
                    }`}
                  >
                    {v}s
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">pick one</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 cursor-pointer">
              <input
                data-testid="new-include-clubs"
                type="checkbox"
                checked={includeClubs}
                onChange={(e) => setIncludeClubs(e.target.checked)}
              />
              <span className="text-sm">Include clubs</span>
            </label>
            <label className="flex items-center gap-2 px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 cursor-pointer">
              <input
                data-testid="new-include-nt"
                type="checkbox"
                checked={includeNT}
                onChange={(e) => setIncludeNT(e.target.checked)}
              />
              <span className="text-sm">Include national teams</span>
            </label>
          </div>

          {err && (
            <div data-testid="new-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {err}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy}
              data-testid="new-submit"
              className="px-6 py-3 rounded-full bg-[var(--accent)] text-black font-bold hover:brightness-110 disabled:opacity-50 transition"
            >
              {busy ? "creating…" : "Create lobby"}
            </button>
            <Link
              to="/dashboard"
              data-testid="new-cancel"
              className="px-5 py-3 rounded-full border border-white/15 hover:bg-white/5 transition"
            >
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
